import { useCallback, useMemo, useState, type FC } from 'react';
import { useParams } from 'react-router-dom';
import 'Lessons/angularModule';
import { angularDirectiveToReact } from 'Angular2reactHelper';
import { useCurrentUser, useNavigationHelper, useOfflineModeManager } from 'FrontRoyalAngular';
import { useTargetBrandConfig } from 'FrontRoyalAngular/useTargetBrandConfig';
import FrontRoyalSpinner from 'FrontRoyalSpinner';
import { type CurrentUser } from 'Users';
import { useAngularContext } from 'AngularContext';
import {
    getExamOpenTime,
    lessonsApi,
    streamDashboardPath,
    type StreamCardAttrs,
    type StreamForDashboard,
    type StreamForStreamDashboard,
} from 'Lessons';
import { useStreamForDashboard } from '../hooks/useStreamForDashboard';
import { useExternalLinksForStream } from '../hooks/useExternalLinksForStream';
import { useOpenExternalLink } from '../hooks/useOpenExternalLink';
import { useIsMobileViewport } from '../hooks/useIsMobileViewport';
import { useStreamWithLiveProgress } from '../hooks/useStreamWithLiveProgress';
import { useStoredStreamForDashboard } from '../hooks/useStoredStreamForDashboard';
import { useStreamRequestOptions } from '../hooks/useStreamRequestOptions';
import { useStreamDashboardRendered } from '../hooks/useStreamDashboardRendered';
import { canLaunchStream as evaluateStreamAccess } from 'ContentAccess';
import { originalUrl } from 'hasResizableImages';
import { toStreamForAngular, toStreamForDashboard } from '../toStreamForDashboard';
import { Footer } from 'Footer';
import { StreamDashboardPage } from './StreamDashboardPage';
import { StreamDashboardLoadError } from './StreamDashboardLoadError';
import { ExternalLinksSection } from './ExternalLinksSection';

/*
    The stream dashboard route. Offline gets its own branch: useContentProgress suspends without a user.
    The LinkedIn button stays an Angular directive.
*/

const LinkedInProfileButtonDirective = angularDirectiveToReact('linkedinProfileButton', { stream: '<' });

const { useGetStreamsByIdsQuery } = lessonsApi;

const StreamDashboardWithStream: FC<{
    stream: StreamForStreamDashboard | null;
    isLoading: boolean;
    hasError: boolean;
    onRetry: () => void;
}> = ({ stream, isLoading, hasError, onRetry }) => {
    const currentUser = useCurrentUser();
    const offlineModeManager = useOfflineModeManager();
    const { loadRoute } = useNavigationHelper();
    const targetBrandConfig = useTargetBrandConfig();
    const $injector = useAngularContext();
    const { userCohortIds, prefLocale } = useStreamRequestOptions();

    // Skipped offline; both lists then render empty.
    const otherStreamIds = useMemo(
        () => [...(stream?.recommendedStreamIds ?? []), ...(stream?.relatedStreamIds ?? [])],
        [stream],
    );

    const { data: otherStreams } = useGetStreamsByIdsQuery(
        { ids: otherStreamIds, userCohortIds, prefLocale },
        { skip: otherStreamIds.length === 0 || offlineModeManager.inOfflineMode },
    );

    // Finishes the navigation timer and emits renderedStreamDashboard, both of which other code
    // still waits on. Above the loading return, so it runs as soon as the stream arrives.
    useStreamDashboardRendered(stream);

    // The directive calls stream.logInfo(), which the vanilla stream lacks.
    const streamForAngular = useMemo(() => (stream ? toStreamForAngular(stream) : null), [stream]);

    const { links, linksByLessonId } = useExternalLinksForStream(stream);
    const openExternalLink = useOpenExternalLink();
    const isMobile = useIsMobileViewport();
    const [showAllExternalLinks, setShowAllExternalLinks] = useState(false);

    const openCourse = useCallback(
        (openStreamId: string) => {
            const card = otherStreams?.contents.lessonStreams.find(other => other.id === openStreamId);
            if (!card) return;

            // A visitor sees the cards but cannot open them (no_user).
            if (!canLaunchCard(card, currentUser)) return;

            loadRoute(streamDashboardPath({ streamTitle: card.title, streamId: card.id }));
        },
        [otherStreams, loadRoute, currentUser],
    );

    /*
        getCards runs evaluateStreamAccess per card, which rebuilds an Iguana stream each time, so
        keep it off the render path.
    */
    const recommendedCourses = useMemo(
        () => getCards(stream?.recommendedStreamIds ?? [], otherStreams, currentUser),
        [stream?.recommendedStreamIds, otherStreams, currentUser],
    );
    const relatedCourses = useMemo(
        () => getCards(stream?.relatedStreamIds ?? [], otherStreams, currentUser),
        [stream?.relatedStreamIds, otherStreams, currentUser],
    );

    // Without this branch a failed fetch spins forever.
    if (hasError) return <StreamDashboardLoadError onRetry={onRetry} />;

    if (isLoading || !stream) return <FrontRoyalSpinner className="fixed" />;

    // dateHelper matches the string format of the rest of the app.
    const dateHelper = $injector.get<{ formattedUserFacingDateTime: (date: Date) => string }>('dateHelper');
    const examOpenTime = getExamOpenTime({ user: currentUser, stream: toStreamForDashboard(stream) });

    return (
        <StreamDashboardPage
            stream={stream}
            recommendedCourses={recommendedCourses}
            relatedCourses={relatedCourses}
            onOpenCourse={openCourse}
            formattedExamOpenTime={examOpenTime ? dateHelper.formattedUserFacingDateTime(examOpenTime) : null}
            brandName={targetBrandConfig?.brandNameShort ?? ''}
            linkedInButton={<LinkedInProfileButtonDirective stream={streamForAngular} />}
            externalLinks={
                links.length > 0 ? (
                    <ExternalLinksSection
                        stream={stream}
                        linksByLessonId={linksByLessonId}
                        isCollapsed={isMobile && !showAllExternalLinks}
                        onShowAll={() => setShowAllExternalLinks(true)}
                        onOpenLink={openExternalLink}
                        brandName={targetBrandConfig?.brandNameShort ?? ''}
                    />
                ) : null
            }
            footer={<Footer />}
        />
    );
};

/*
    Only surface a recommended or related course the student can actually open. A student in several
    programs, or holding an offer, can have a course come back from the index through a program that
    does not unlock it, which would otherwise render as a locked, unclickable card.
    See https://trello.com/c/ywCzn8nw.

    A signed-out visitor skips the canLaunch check: they see the cards and the sign-up modal takes over
    when they click one.
*/
function getCards(
    ids: string[],
    otherStreams: { contents: { lessonStreams: StreamCardAttrs[] } } | undefined,
    currentUser: CurrentUser | null,
) {
    if (!otherStreams) return [];

    return ids
        .map(id => otherStreams.contents.lessonStreams.find(other => other.id === id))
        .filter((other): other is StreamCardAttrs => {
            if (!other || other.comingSoon) return false;
            if (!currentUser) return true;
            return canLaunchCard(other, currentUser);
        })
        .map(other => ({
            id: other.id,
            title: other.title,
            imageSrc: originalUrl(other.image) ?? '',
        }));
}

const StudentStreamDashboard: FC<{ streamId: string }> = ({ streamId }) => {
    const { stream, isLoading, hasError, retry } = useStreamForDashboard(streamId);
    const streamWithProgress = useStreamWithLiveProgress(stream);
    return (
        <StreamDashboardWithStream
            stream={streamWithProgress}
            isLoading={isLoading}
            hasError={hasError}
            onRetry={retry}
        />
    );
};

const VisitorStreamDashboard: FC<{ streamId: string }> = ({ streamId }) => {
    const { stream, isLoading, hasError, retry } = useStreamForDashboard(streamId);
    return <StreamDashboardWithStream stream={stream} isLoading={isLoading} hasError={hasError} onRetry={retry} />;
};

/*
    Signed-in students and visitors take different branches, because only the student path may call
    useContentProgress: it suspends forever without a current user.

    A student's progress comes from ContentProgress rather than from the payload. The payload's copy is a
    snapshot that RTK Query then serves from cache, so it does not change after a lesson is started. See
    useStreamWithLiveProgress.
*/
const OnlineStreamDashboard: FC<{ streamId: string }> = ({ streamId }) => {
    const currentUser = useCurrentUser();

    if (currentUser) return <StudentStreamDashboard streamId={streamId} />;
    return <VisitorStreamDashboard streamId={streamId} />;
};

const OfflineStreamDashboard: FC<{ streamId: string }> = ({ streamId }) => {
    const { stream, isLoading, hasError, retry } = useStoredStreamForDashboard(streamId);
    return <StreamDashboardWithStream stream={stream} isLoading={isLoading} hasError={hasError} onRetry={retry} />;
};

export const StreamDashboardComponent: FC = () => {
    const { streamId } = useParams<{ streamId: string }>();
    const offlineModeManager = useOfflineModeManager();

    // Keyed on streamId so another course gets a fresh tree; mount-seeded state does not carry over.
    if (offlineModeManager.inOfflineMode) return <OfflineStreamDashboard key={streamId} streamId={streamId!} />;
    return <OnlineStreamDashboard key={streamId} streamId={streamId!} />;
};

/*
    A card from the index call, in the shape the access check wants.

    The card is deliberately a narrow payload, so two fields are filled rather than fetched: `locale`,
    and each lesson's comingSoon and unrestricted. The stream-level access check reads neither. It works
    off the stream's locale pack id and the user's cohorts, which the card does carry. Widening the
    request to avoid these placeholders would cost a bigger payload on every dashboard load.
*/
function cardToStreamForDashboard(card: StreamCardAttrs): StreamForDashboard {
    return {
        id: card.id,
        title: card.title,
        locale: '',
        exam: card.exam,
        chapters: card.chapters,
        localePackId: card.localePackId,
        image: card.image,
        requiresProctoring: false,
        comingSoon: card.comingSoon,
        lessons: card.lessons.map(lesson => ({
            id: lesson.id,
            title: lesson.title,
            localePackId: lesson.localePack.id,
            test: lesson.test,
            assessment: lesson.assessment,
            comingSoon: false,
            unrestricted: false,
        })),
    };
}

function canLaunchCard(card: StreamCardAttrs, user: CurrentUser | null) {
    return evaluateStreamAccess({
        stream: cardToStreamForDashboard(card),
        user,
        streamProgress: card.lessonStreamsProgress,
    }).canLaunch;
}
